import { gql } from 'graphql-request';
import { BlogNewsListCardData, BlogNewsListDataContainer } from '../blog-news/blog-news';
import { EventListCardData, EventListDataContainer } from '../event/event-types';
import { ContentHubProps } from './content-hub-types';

export type PostType = 'blog-and-news' | 'event';

export type EntriesShape = {
    blogPosts?: BlogNewsListDataContainer[];
    blogPostsWithTags?: BlogNewsListDataContainer[];
    events?: EventListDataContainer[];
    eventsWithTags?: EventListDataContainer[];
};

export type CustomPostsShape = {
    posts: Array<{ content?: BlogNewsListCardData | EventListCardData }>;
};

export type QueryVariables = {
    limit: number;
    blogTags?: string[];
    eventTags?: string[];
    fromDate?: string;
};

export const GET_CUSTOM_POSTS_QUERY = gql`
    query GetCustomPosts($keys: [String]) {
        posts: resources(keys: $keys) { content }
    }
`;

const buildResourceFragment = (
    alias: string,
    type: PostType,
    { withTags, withFromDate }: { withTags?: boolean; withFromDate?: boolean },
) => {
    const args = [`type: "${type}"`];
    if (withTags) {
        args.push(`tags: ${type === 'event' ? '$eventTags' : '$blogTags'}`);
    }
    args.push('limit: $limit', 'order: DESC');
    if (withFromDate) {
        args.push('fromDate: $fromDate');
    }
    return `${alias}: resources(${args.join(', ')}) {
        key
        meta { postTitle categories description featuredImage extra }
    }`;
};

type Path = { fragment: string; declaresTagsVar: boolean; tags: string[] };

const computePath = (type: PostType, props: ContentHubProps): Path => {
    const aliasBase = type === 'event' ? 'events' : 'blogPosts';
    const withFromDate = type === 'event';

    if (props.mixedContent) {
        const matching = props.cards.filter(
            (c) => !c.customPostLink?.url && c.posttype?.value === type,
        );
        if (matching.length === 0) {
            return { fragment: '', declaresTagsVar: false, tags: [] };
        }

        const tags = Array.from(
            new Set(matching.map((c) => c.tag?.slug).filter(Boolean) as string[]),
        );
        const fragments = [buildResourceFragment(aliasBase, type, { withFromDate })];
        if (tags.length > 0) {
            fragments.push(buildResourceFragment(`${aliasBase}WithTags`, type, { withTags: true, withFromDate }));
        }
        return { fragment: fragments.join('\n'), declaresTagsVar: tags.length > 0, tags };
    }

    if (props.contentType?.posttype?.value !== type) {
        return { fragment: '', declaresTagsVar: false, tags: [] };
    }
    const slug = props.contentType.tag?.slug;
    return {
        fragment: [
            buildResourceFragment(aliasBase, type, { withFromDate }),
            buildResourceFragment(`${aliasBase}WithTags`, type, { withTags: true, withFromDate }),
        ].join('\n'),
        declaresTagsVar: true,
        tags: slug ? [slug] : [],
    };
};

/** Build the GraphQL query + matching variables for a `ContentHub` instance.
 *  Loads both post types in one round-trip (each as a separate alias), with
 *  optional tag-filtered companion fragments so we can fall back to the wider
 *  pool if the tagged set comes back empty. Larger pool than NUMBER_OF_CARDS
 *  so we have backups after audience / on-demand / fully-booked filtering. */
export const buildEntriesQuery = (
    props: ContentHubProps,
): { query: string; variables: QueryVariables; hasEntries: boolean } => {
    const blogPath = computePath('blog-and-news', props);
    const eventPath = computePath('event', props);

    const queryArgs = ['$limit: Int'];
    if (blogPath.declaresTagsVar) {
        queryArgs.push('$blogTags: [String]');
    }
    if (eventPath.declaresTagsVar) {
        queryArgs.push('$eventTags: [String]');
    }
    if (eventPath.fragment) {
        queryArgs.push('$fromDate: String');
    }

    // When every card is a custom post link (served by GET_CUSTOM_POSTS_QUERY),
    // both paths come back empty and there is nothing to fetch here. Signal that
    // so the caller can skip firing an empty query, which the API gateway rejects
    // with a 403 and crashes SSR.
    const fragments = [blogPath.fragment, eventPath.fragment].filter(Boolean);
    const hasEntries = fragments.length > 0;

    const query = `query GetEntries(${queryArgs.join(', ')}) {
        ${fragments.join('\n')}
    }`;

    const variables: QueryVariables = {
        limit: 50,
        fromDate: new Date().toDateString(),
        blogTags: blogPath.tags,
        eventTags: eventPath.tags,
    };

    return { query, variables, hasEntries };
};
